/**
 * The message staff see when an identity lookup (NIN, BVN, ...) fails.
 *
 * The lookup goes web -> core -> the country-data identity service -> the verification provider. When it
 * fails, the text that comes back is whatever the failing layer produced: "Unknown error occurred", or
 * core's wrapper "Identity verification failed: 404 Not Found: ...", which tell staff nothing they can act
 * on. A specific, readable reason from the provider (for example "NIN not found") is kept as it is.
 */

export interface IdentityFailure {
  /** HTTP status, when the failure was an HTTP error (0 = the request never got an answer) */
  status?: number;
  /** The API's message, when it sent one; `error.message` on an HttpErrorResponse is also read */
  message?: string;
  error?: { message?: string } | null;
}

// Wording that describes the plumbing, not the person's ID.
const NOT_USEFUL = /unknown error|error occurred|identity verification failed|internal server error|bad gateway|service unavailable|gateway time-?out|no response from|not configured|null|undefined|exception/i;

const MAX_PROVIDER_MESSAGE_LENGTH = 140;

export function identityVerificationMessage(failure: IdentityFailure | null | undefined, identifierType?: string): string {
  const what = (identifierType || '').trim() || $localize`:@@identity_default_label:ID`;
  const status = failure?.status;
  const reason = String(failure?.error?.message ?? failure?.message ?? '').trim();

  if (status === 0 || status === 502 || status === 503 || status === 504) {
    return $localize`:@@identity_service_down:The ${what}:what: verification service is not responding right now. Please try again in a moment.`;
  }

  if (status === 429) {
    return $localize`:@@identity_rate_limited:Too many verification attempts. Please wait a moment and try again.`;
  }

  if (reason && reason.length <= MAX_PROVIDER_MESSAGE_LENGTH && !NOT_USEFUL.test(reason)) {
    return reason;
  }

  return $localize`:@@identity_not_verified:We couldn't verify this ${what}:what:. Check the number is correct and try again. If it keeps failing, use another ID or contact support.`;
}
